import { focusManager } from '@tanstack/react-query';
import { trpc } from '@/shared/api/trpc';

/**
 * Adaptive cadence. The event that creates a notification happens in a
 * customer's browser, so polling is the only way to learn about it — but how
 * fast we need to learn depends entirely on whether anyone is looking.
 *
 * Focused: 20s, so a toast lands within a few seconds of the user's attention
 * being on the app. Background: 60s, unchanged, so an idle tab costs no more
 * than it did before. Two small queries at 3/min per ACTIVE tab is a trivial
 * load for an internal console, and idle tabs — the common case — are untouched.
 */
export const NOTIFICATION_POLL_ACTIVE_MS = 20 * 1000;
export const NOTIFICATION_POLL_IDLE_MS = 60 * 1000;

/** Re-evaluated by query-core each time the interval is rescheduled. */
export const notificationPollInterval = (): number =>
  focusManager.isFocused()
    ? NOTIFICATION_POLL_ACTIVE_MS
    : NOTIFICATION_POLL_IDLE_MS;

/** Kept as the worst-case ceiling used for staleness and by tests. */
export const NOTIFICATION_POLL_MS = NOTIFICATION_POLL_IDLE_MS;

// Unread badge count for the signed-in user.
//
// Polled, unlike the support/incident badges: those only ever change as a
// result of the viewer's OWN mutations, so invalidation alone keeps them
// correct. A notification is created by someone ELSE's action (a customer
// opening a Traveller Preview), so nothing in this tab would ever invalidate
// it. 60s + invalidation matches the operations count widgets.
//
// `refetchIntervalInBackground` is REQUIRED, not a nicety. query-core only
// calls the interval's fetch when `refetchIntervalInBackground ||
// focusManager.isFocused()` (queryObserver.ts). The event that produces a
// notification happens in someone else's browser, so the console tab is very
// often unfocused exactly when the count changes; without this the timer ticks
// and does nothing, and — because the app sets `refetchOnWindowFocus: false`
// globally and `staleTime` suppresses the remount refetch — the badge would
// only ever update on a full page reload.
//
// `skipBatch` keeps this small poll off the shared batch so it cannot delay a
// page's real queries.
export const useUnreadCount = (enabled = true) =>
  trpc.notifications.unreadCount.useQuery(undefined, {
    enabled,
    refetchInterval: notificationPollInterval,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    // Returning to the tab should not wait out the interval. Paired with the
    // staleTime below this refetches whenever the user has been away long
    // enough for anything to have changed, and stays quiet on a quick alt-tab.
    // Below the interval so a focus refetch can actually fire, but high enough
    // that `AppLayout` remounting on every page navigation does not.
    staleTime: NOTIFICATION_POLL_MS / 2,
    trpc: { context: { skipBatch: true } },
  });
